import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, Cpu, ExternalLink, FlaskConical, Loader2, Moon, Play, Plus, RefreshCw, Search, ShieldAlert, Skull, Sparkles, X } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Toggle } from "@/components/kit";

export const STATUS_LABEL = {
  not_tested: ["Not tested", "bg-zinc-800 text-zinc-400"], compat_tested: ["Compatibility tested", "bg-sky-500/15 text-sky-400"],
  protection_tested: ["Protection tested", "bg-sky-500/15 text-sky-400"], approved: ["Approved", "bg-emerald-500/15 text-emerald-400"],
  restricted: ["Restricted", "bg-amber-500/15 text-amber-400"], compat_issue: ["Compatibility issue", "bg-red-500/15 text-red-400"],
};

export function StatusPill({ g, blocked }) {
  const [label, cls] = g.hidden ? ["Disabled", "bg-red-500/15 text-red-400"] : blocked ? ["Website blocked", "bg-red-500/15 text-red-400"]
    : STATUS_LABEL[g.status] || STATUS_LABEL.not_tested;
  return <span data-testid={`cloud-status-${g.kind}-${g.id}`} className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${cls}`}>{label}{g.cloud_enabled && !g.hidden && !blocked ? " · Live" : ""}</span>;
}

function CheckResult({ c }) {
  if (!c) return <span className="text-xs text-zinc-500">No check run yet</span>;
  const ok = (v) => (v ? "text-emerald-400" : "text-red-400");
  return (
    <div className="text-xs text-zinc-400 space-y-0.5" data-testid="cloud-check-result">
      <div><span className={ok(c.compat_ok)}>Compatibility {c.compat_ok ? "passed" : "failed"}</span> · <span className={ok(c.protection_ok)}>Protection {c.protection_ok ? "passed" : "failed"}</span> · {new Date(c.ran_at).toLocaleString()}</div>
      <div>Load {c.load_seconds}s · {c.frames ?? 0} frames · WebGL {c.webgl ? "yes" : "no"} · canvases {c.canvases ?? 0} · iframes {c.iframes ?? 0}</div>
      {c.counts && <div>Blocked: {c.counts.blocked_ads} ads · {c.counts.blocked_tracking} trackers · {c.counts.popups} pop-ups · {c.counts.redirects} redirects · {c.counts.blocked_private} internal</div>}
      {c.metadata && <div>Network isolation: metadata {c.metadata} · localhost {c.localhost} · private LAN {c.private_net} · pop-up shim {c.popup_shim ? "active" : "missing"}</div>}
      {c.error && <div className="text-red-400 break-words">{c.error.split("\n")[0]}{c.infra_error ? " (server problem — not counted against this game)" : ""}</div>}
    </div>
  );
}

function ControlsEditor({ g, base, onSaved }) {
  const [rows, setRows] = useState(g.controls || []);
  const [busy, setBusy] = useState(false);
  const tid = `${g.kind}-${g.id}`;
  const set = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const draft = async () => {
    setBusy(true);
    try { const { data } = await api.post(`${base}/controls/draft`); setRows(data.controls); toast.success("AI draft ready — check it, then save"); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const save = async () => {
    try { await api.put(base, { controls: rows.filter((r) => r.keys.trim() && r.action.trim()) }); toast.success("Controls saved"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const inp = "bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1.5 text-sm text-white min-w-0";
  return (
    <div className="mt-3 border-t border-zinc-800 pt-3 space-y-2" data-testid={`cloud-controls-${tid}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs text-zinc-400">Controls shown to players before the game starts {rows.length ? "" : "(none — players see a general guide)"}</span>
        <button data-testid={`cloud-controls-draft-${tid}`} onClick={draft} disabled={busy} className="text-xs border border-zinc-700 rounded-lg px-3 py-1.5 hover:bg-zinc-800 flex items-center gap-1.5 disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 text-volt" />} Draft with AI</button>
      </div>
      {rows.map((r, i) => (
        <div key={i} className="flex gap-2">
          <input data-testid={`cloud-control-keys-${tid}-${i}`} value={r.keys} maxLength={40} onChange={(e) => set(i, "keys", e.target.value)} placeholder="Keys" className={`${inp} w-36`} />
          <input data-testid={`cloud-control-action-${tid}-${i}`} value={r.action} maxLength={80} onChange={(e) => set(i, "action", e.target.value)} placeholder="Action" className={`${inp} flex-1`} />
          <button data-testid={`cloud-control-remove-${tid}-${i}`} onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label="Remove" className="text-zinc-500 hover:text-red-400"><X className="w-4 h-4" /></button>
        </div>
      ))}
      <div className="flex gap-2">
        {rows.length < 12 && <button data-testid={`cloud-control-add-${tid}`} onClick={() => setRows([...rows, { keys: "", action: "" }])} className="text-xs text-volt hover:underline flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add control</button>}
        <button data-testid={`cloud-controls-save-${tid}`} onClick={save} className="ml-auto bg-volt text-volt-ink font-semibold text-sm rounded-lg px-4 py-1.5 hover:bg-volt-hover">Save controls</button>
      </div>
    </div>
  );
}

function GameEditor({ g, blocked, onSaved, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState({ domains: g.allowed_domains.join("\n"), ad_issues: g.ad_issues, compat_issues: g.compat_issues });
  const [busy, setBusy] = useState("");
  const base = `/admin/cloud/games/${g.kind}/${g.id}`;
  const put = async (patch, msg) => {
    try { await api.put(base, patch); toast.success(msg); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const check = async () => {
    setBusy("check");
    try {
      await api.post(`/admin/cloud/check/${g.kind}/${g.id}`);
      toast.info("Check running — about 30–60 seconds");
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data } = await api.get(`/admin/cloud/check/${g.kind}/${g.id}`);
        if (!data.running) break;
      }
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const launch = async () => {
    try { const { data } = await api.post(`/admin/cloud/test-launch/${g.kind}/${g.id}`); window.open(data.url, "_blank", "noopener"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const tid = `${g.kind}-${g.id}`;
  return (
    <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950" data-testid={`cloud-game-${tid}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex items-start gap-3">
          {onSelect && <input type="checkbox" data-testid={`cloud-select-${tid}`} checked={selected} onChange={(e) => onSelect(e.target.checked)} aria-label={`Select ${g.title}`} className="mt-1 w-4 h-4 accent-[#0EA5E9]" />}
          <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap"><span className="font-medium truncate max-w-full">{g.title}</span> <StatusPill g={g} blocked={blocked} /></div>
          <a href={g.url} target="_blank" rel="noopener noreferrer" className="text-xs text-zinc-500 hover:text-volt inline-flex items-center gap-1">{g.host} <ExternalLink className="w-3 h-3" /></a>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid={`cloud-check-${tid}`} onClick={check} disabled={!!busy} className="text-xs border border-zinc-700 rounded-lg px-3 py-1.5 hover:bg-zinc-800 flex items-center gap-1.5 disabled:opacity-50">
            {busy === "check" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FlaskConical className="w-3.5 h-3.5" />} Run check</button>
          <button data-testid={`cloud-launch-${tid}`} onClick={launch} className="text-xs border border-zinc-700 rounded-lg px-3 py-1.5 hover:bg-zinc-800 flex items-center gap-1.5"><Play className="w-3.5 h-3.5" /> Test play</button>
          <select data-testid={`cloud-status-select-${tid}`} value={g.status} onChange={(e) => put({ status: e.target.value }, "Status updated")} className="text-xs bg-zinc-900 border border-zinc-700 rounded-lg px-2 py-1.5">
            {Object.entries(STATUS_LABEL).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <label className="flex items-center gap-2 text-xs text-zinc-400">Protected play
            <Toggle testid={`cloud-enable-${tid}`} checked={g.cloud_enabled} label="Protected play" onChange={(v) => put({ cloud_enabled: v }, v ? "Protected play enabled" : "Protected play disabled")} />
          </label>
          <button data-testid={`cloud-edit-${tid}`} onClick={() => setOpen((o) => !o)} className="text-xs text-volt hover:underline">{open ? "Close" : "Domains, notes & controls"}</button>
        </div>
      </div>
      <div className="mt-2"><CheckResult c={g.last_check} /></div>
      {g.pulled && !g.cloud_enabled && <div data-testid={`cloud-pulled-${tid}`} className="mt-2 text-xs text-red-400">Pulled by nightly check on {new Date(g.pulled.at).toLocaleString()} — {g.pulled.reason}. Fix and re-check, then re-enable.</div>}
      {g.cloud_enabled && g.fail_streak === 1 && <div data-testid={`cloud-warned-${tid}`} className="mt-2 text-xs text-amber-400">Failed last night's check — will be pulled if it fails again tonight.</div>}
      {open && <ControlsEditor g={g} base={base} onSaved={onSaved} />}
      {open && (
        <div className="mt-3 grid md:grid-cols-3 gap-3">
          {[["domains", "Extra permitted domains (one per line)"], ["ad_issues", "Known advertising issues"], ["compat_issues", "Known compatibility issues"]].map(([k, l]) => (
            <label key={k} className="text-xs text-zinc-400 space-y-1"><span>{l}</span>
              <textarea data-testid={`cloud-${k}-${tid}`} rows={4} value={d[k]} onChange={(e) => setD({ ...d, [k]: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-2 text-sm text-white" />
            </label>
          ))}
          <button data-testid={`cloud-save-${tid}`} onClick={() => put({ allowed_domains: d.domains.split(/\s+/).filter(Boolean), ad_issues: d.ad_issues, compat_issues: d.compat_issues }, "Saved")}
            className="md:col-span-3 justify-self-start bg-volt text-volt-ink font-semibold text-sm rounded-lg px-4 py-2 hover:bg-volt-hover">Save domains & notes</button>
        </div>
      )}
    </div>
  );
}

function BulkPanel({ selected, clear, onDone }) {
  const [st, setSt] = useState(null);
  const poll = useCallback(async () => {
    const { data } = await api.get("/admin/cloud/bulk-check");
    setSt(data);
    return data.running;
  }, []);
  useEffect(() => {
    let live = true;
    const loop = async () => { while (live && (await poll().catch(() => false))) await new Promise((r) => setTimeout(r, 3000)); if (live) onDone(); };
    poll().then((running) => running && loop()).catch(() => {});
    return () => { live = false; };
  }, [poll, onDone]);
  const go = async (body) => {
    if (!window.confirm("Run checks now? Every game that passes BOTH checks will be approved and playable from Discord straight away.")) return;
    try {
      const { data } = await api.post("/admin/cloud/bulk-check", body);
      toast.info(`Checking ${data.total} game(s) — about 30–60 s each`);
      clear();
      while (await poll()) await new Promise((r) => setTimeout(r, 3000));
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const pct = st?.total ? Math.round((st.done / st.total) * 100) : 0;
  return (
    <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950 mb-4 space-y-3" data-testid="cloud-bulk-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <button data-testid="cloud-bulk-selected" disabled={!selected.length || st?.running} onClick={() => go({ game_ids: selected })}
          className="text-sm bg-volt text-volt-ink font-semibold rounded-lg px-4 py-2 disabled:bg-zinc-800 disabled:text-zinc-500">Check & approve selected ({selected.length})</button>
        <button data-testid="cloud-bulk-untested" disabled={st?.running} onClick={() => go({ all_untested: true })}
          className="text-sm border border-zinc-700 rounded-lg px-4 py-2 hover:bg-zinc-800 disabled:opacity-40">Check all untested games</button>
        <span className="text-xs text-zinc-500">Games passing compatibility + protection are approved and enabled automatically. Games that fail compatibility are marked "Compatibility issue"; games that only fail protection stay "Compatibility tested" and unpublished.</span>
      </div>
      {st && (st.running || st.finished_at) && (
        <div data-testid="cloud-bulk-progress" className="text-sm space-y-2">
          <div className="flex justify-between text-xs text-zinc-400"><span>{st.running ? `Checking: ${st.current || "…"}` : "Last bulk check finished"}</span><span>{st.done}/{st.total}</span></div>
          <div className="h-2 rounded-full bg-zinc-800 overflow-hidden"><div className="h-full bg-volt transition-[width]" style={{ width: `${pct}%` }} /></div>
          <div className="text-xs"><span className="text-emerald-400">{st.approved.length} approved</span> · <span className="text-red-400">{st.failed.length} failed</span></div>
          {!!st.failed.length && <ul className="text-xs text-zinc-500 max-h-28 overflow-y-auto">{st.failed.map((f) => <li key={f.id}>{f.title} — {f.reason}</li>)}</ul>}
        </div>
      )}
    </div>
  );
}

export function CloudGames() {
  const [sel, setSel] = useState([]);
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const load = useCallback(() => api.get("/admin/cloud/games", { params: { search: q, page, status } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [q, page, status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />;
  const blocked = (g) => data.hidden_domains.some((d) => g.host === d || g.host.endsWith(`.${d}`));
  return (
    <div className="space-y-6">
      <section>
        <h3 className="font-head font-semibold mb-1">Development test sites</h3>
        <p className="text-xs text-zinc-500 mb-3">Not part of the production catalogue. Add them to your GitHub list and import with Refresh when ready.</p>
        <div className="space-y-3">{data.test_targets.map((g) => <GameEditor key={g.id} g={g} blocked={blocked(g)} onSaved={load} />)}</div>
      </section>
      <section>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h3 className="font-head font-semibold">Catalogue games ({data.total})</h3>
          <div className="flex gap-2 flex-wrap">
            <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-zinc-500" />
              <input data-testid="cloud-games-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search…" className="bg-zinc-900 border border-zinc-800 rounded-lg pl-8 pr-3 py-2 text-sm w-40 sm:w-48" /></div>
            <select data-testid="cloud-games-status-filter" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="bg-zinc-900 border border-zinc-800 rounded-lg px-2 text-sm">
              <option value="">All statuses</option>{Object.entries(STATUS_LABEL).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
        </div>
        <BulkPanel selected={sel} clear={() => setSel([])} onDone={load} />
        <label className="flex items-center gap-2 text-xs text-zinc-400 mb-2"><input type="checkbox" data-testid="cloud-select-page" className="w-4 h-4 accent-[#0EA5E9]"
          checked={!!data.games.length && data.games.every((g) => sel.includes(g.id))}
          onChange={(e) => setSel((s) => (e.target.checked ? [...new Set([...s, ...data.games.map((g) => g.id)])] : s.filter((i) => !data.games.some((g) => g.id === i))))} /> Select this page</label>
        <div className="space-y-3">{data.games.map((g) => <GameEditor key={g.id} g={g} blocked={blocked(g)} onSaved={load}
          selected={sel.includes(g.id)} onSelect={(on) => setSel((s) => (on ? [...s, g.id] : s.filter((i) => i !== g.id)))} />)}</div>
        <div className="flex items-center gap-3 mt-4 text-sm">
          <button data-testid="cloud-games-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="border border-zinc-800 rounded-lg px-3 py-1.5 disabled:opacity-40">Previous</button>
          <span className="text-zinc-500">Page {data.page} of {data.pages}</span>
          <button data-testid="cloud-games-next" disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="border border-zinc-800 rounded-lg px-3 py-1.5 disabled:opacity-40">Next</button>
        </div>
      </section>
    </div>
  );
}

export function CloudSessions() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/admin/cloud/sessions").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  const kill = async (id) => {
    if (!window.confirm("Terminate this session?")) return;
    try { await api.delete(`/admin/cloud/sessions/${id}`); toast.success("Session terminated"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />;
  const mb = (b) => `${(b / 1048576).toFixed(1)} MB`;
  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-head font-semibold mb-3">Active sessions ({d.active.length})</h3>
        {!d.active.length ? <p className="text-sm text-zinc-500" data-testid="cloud-no-sessions">No one is playing right now.</p> : (
          <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="cloud-sessions-table">
            <thead className="text-xs text-zinc-500 text-left"><tr><th className="py-2 pr-3">Game</th><th className="pr-3">Owner</th><th className="pr-3">State</th><th className="pr-3">Duration</th><th className="pr-3">Stream</th><th className="pr-3">Blocked</th><th /></tr></thead>
            <tbody>{d.active.map((s) => (
              <tr key={s.id} className="border-t border-zinc-800" data-testid={`cloud-session-${s.id}`}>
                <td className="py-2 pr-3">{s.game.title}</td><td className="pr-3">{s.owner?.name || s.owner?.id} <span className="text-zinc-500 text-xs">({s.owner?.via})</span></td>
                <td className="pr-3">{s.state}{s.connected ? "" : " · offline"}</td><td className="pr-3">{Math.round(s.duration_s / 60)} min</td>
                <td className="pr-3">{s.frames} frames · {mb(s.bytes_out)}</td>
                <td className="pr-3 text-xs">{s.counts.blocked_ads + s.counts.blocked_tracking} filtered · {s.counts.popups} pop-ups · {s.counts.redirects} redirects</td>
                <td><button data-testid={`cloud-kill-${s.id}`} onClick={() => kill(s.id)} className="text-red-400 hover:text-red-300 flex items-center gap-1 text-xs"><Skull className="w-3.5 h-3.5" /> End</button></td>
              </tr>))}</tbody></table></div>
        )}
      </div>
      <div>
        <h3 className="font-head font-semibold mb-3">Recent sessions</h3>
        <div className="space-y-1 text-xs">{d.recent.map((s) => (
          <div key={s.id} className="flex flex-wrap gap-x-3 text-zinc-400 border-b border-zinc-800/60 py-1.5">
            <span className="text-white">{s.game?.title}</span><span>{s.owner?.name || s.owner?.id}</span><span>{Math.round((s.duration_s || 0) / 60)} min</span>
            <span>{mb(s.bytes_out || 0)} out · {mb(s.net_down || 0)} downloaded</span><span className={`min-w-0 break-words line-clamp-2 ${s.error ? "text-red-400" : ""}`}>{s.end_reason}{s.error ? ` — ${s.error.split("\n")[0]}` : ""}</span>
          </div>))}</div>
      </div>
    </div>
  );
}

const KINDS = ["", "blocked_ads", "blocked_tracking", "popup", "redirect", "blocked_domain", "security", "session_start", "session_end", "session_error", "worker_crash", "browser_install", "nightly_pulled"];

function NightlyCard() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/admin/cloud/nightly").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!d?.running) return; const t = setInterval(load, 4000); return () => clearInterval(t); }, [d?.running, load]);
  const run = async () => { try { await api.post("/admin/cloud/nightly/run"); toast.info("Re-checking approved games now"); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!d) return null;
  const l = d.last || {};
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-5" data-testid="cloud-nightly">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <h3 className="font-head font-semibold flex items-center gap-2"><Moon className="w-4 h-4" /> Nightly auto-checks</h3>
        <button data-testid="cloud-nightly-run" onClick={run} disabled={d.running} className="text-xs border border-zinc-700 rounded-lg px-3 py-1.5 hover:bg-zinc-800 flex items-center gap-1.5 disabled:opacity-50">
          {d.running && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {d.running ? "Checking…" : "Run now"}</button>
      </div>
      <p className="text-xs text-zinc-500 mb-3">{d.schedule}: every approved game is re-checked. A game failing 2 nights in a row is pulled from protected play. Server problems (e.g. Chromium missing) are never counted against a game.</p>
      {!l.started_at ? <p className="text-sm text-zinc-500" data-testid="cloud-nightly-none">No nightly run yet.</p> : (
        <div className="text-sm space-y-2" data-testid="cloud-nightly-last">
          <div className="text-xs text-zinc-400">Last run {new Date(l.started_at).toLocaleString()} ({l.trigger}) · {l.checked} checked · <span className="text-emerald-400">{l.passed} passed</span> · <span className="text-amber-400">{l.warned?.length || 0} first fail</span> · <span className="text-red-400">{l.pulled?.length || 0} pulled</span> · {l.skipped?.length || 0} skipped</div>
          {[["pulled", "text-red-400"], ["warned", "text-amber-400"], ["skipped", "text-zinc-500"]].map(([k, c]) => !!l[k]?.length && (
            <ul key={k} className={`text-xs ${c} max-h-24 overflow-y-auto`} data-testid={`cloud-nightly-${k}`}>{l[k].map((x) => <li key={x.id} className="break-words">{x.title} — {x.reason}</li>)}</ul>))}
        </div>
      )}
      {!!d.pulled_games.length && (
        <div className="mt-3 border-t border-zinc-800 pt-3" data-testid="cloud-nightly-pulled-alert">
          <div className="text-xs font-semibold text-red-400 mb-1">Pulled overnight — still offline ({d.pulled_games.length})</div>
          <ul className="text-xs text-zinc-400 space-y-0.5">{d.pulled_games.map((p) => <li key={p.id}>{p.title} · {new Date(p.at).toLocaleDateString()} — {p.reason}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

export function CloudEvents() {
  const [kind, setKind] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/cloud/events", { params: { kind, limit: 200 } }).then((r) => setD(r.data)).catch(() => {}); }, [kind]);
  return (
    <div>
      <div className="flex gap-1 flex-wrap mb-4">
        {KINDS.map((k) => <button key={k || "all"} data-testid={`cloud-events-${k || "all"}`} onClick={() => setKind(k)} className={`text-xs px-3 py-1.5 rounded-lg ${kind === k ? "bg-volt/15 text-volt" : "bg-zinc-900 text-zinc-400"}`}>{k ? k.replace("_", " ") : "All"} {d?.totals?.[k] ? `(${d.totals[k]})` : ""}</button>)}
      </div>
      {!d ? <Loader2 className="w-5 h-5 animate-spin text-zinc-500" /> : !d.events.length ? <p className="text-sm text-zinc-500">No events yet.</p> : (
        <div className="space-y-1 text-xs font-mono" data-testid="cloud-events-list">{d.events.map((e, i) => (
          <div key={i} className="flex gap-3 border-b border-zinc-800/60 py-1.5 min-w-0">
            <span className="text-zinc-500 shrink-0">{new Date(e.ts).toLocaleTimeString()}</span>
            <span className={`shrink-0 ${e.severity === "error" ? "text-red-400" : e.severity === "warning" ? "text-amber-400" : "text-zinc-400"}`}>{e.kind}</span>
            <span className="text-zinc-300 truncate max-w-[30%] shrink-0">{e.host}</span><span className="text-zinc-500 truncate min-w-0">{e.game_title} — {e.detail}</span>
          </div>))}</div>
      )}
    </div>
  );
}

const SWITCHES = [["enabled", "Protected browser"], ["ad_filter", "Advertising filter"], ["tracking", "Tracking protection"], ["popups", "Pop-up & new-window blocking"], ["redirects", "Redirect protection"], ["audio", "Game sound streaming"], ["allow_1080", "Allow 1080p streams"]];
const NUMBERS = [["max_sessions", "Max concurrent sessions", 1, 100], ["idle_minutes", "Idle timeout (min)", 1, 120], ["max_minutes", "Max session length (min)", 5, 600], ["bandwidth_kbps", "Bandwidth cap per session (kbps)", 500, 50000]];

export function CloudOverview() {
  const [s, setS] = useState(null);
  const [h, setH] = useState(null);
  const [lt, setLt] = useState([]);
  const [n, setN] = useState(1);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => {
    api.get("/admin/cloud/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/admin/cloud/health").then((r) => setH(r.data)).catch(() => {});
    api.get("/admin/cloud/load-tests").then((r) => setLt(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const put = async (patch) => { try { const { data } = await api.put("/admin/cloud/settings", patch); setS(data); toast.success("Saved"); } catch (e) { toast.error(errMsg(e)); } };
  const run = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { toast.error(errMsg(e)); } setBusy(""); load(); };
  if (!s) return <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />;
  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-5 space-y-3">
          <h3 className="font-head font-semibold">Protection settings</h3>
          {SWITCHES.map(([k, l]) => <div key={k} className="flex items-center justify-between text-sm"><span>{l}</span><Toggle testid={`cloud-setting-${k}`} label={l} checked={s[k]} onChange={(v) => put({ [k]: v })} /></div>)}
          {NUMBERS.map(([k, l, min, max]) => (
            <div key={k} className="flex items-center justify-between text-sm gap-3"><span>{l}</span>
              <input data-testid={`cloud-setting-${k}`} type="number" min={min} max={max} defaultValue={s[k]} onBlur={(e) => Number(e.target.value) !== s[k] && put({ [k]: Number(e.target.value) })} className="w-24 bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1 text-right" /></div>
          ))}
        </div>
        <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between"><h3 className="font-head font-semibold flex items-center gap-2"><Cpu className="w-4 h-4" /> Browser worker health</h3>
            <button data-testid="cloud-health-refresh" onClick={load} className="text-xs text-zinc-400 hover:text-white"><RefreshCw className="w-3.5 h-3.5" /></button></div>
          {h && (
            <div className="grid grid-cols-2 gap-2 text-sm" data-testid="cloud-health">
              {[["Worker", h.browser_running ? "Running" : "Idle (starts on demand)"], ["Active / queued", `${h.active} / ${h.queued} (max ${h.max_sessions})`],
                ["Chromium processes", h.chromium_processes], ["Chromium CPU", `${h.chromium_cpu_percent}%`], ["Chromium memory", `${h.chromium_rss_mb} MB`],
                ["Server CPU", `${h.system_cpu_percent}% of ${h.cpu_count} cores`], ["Server memory used", `${h.system_mem_percent}%`], ["Worker crashes", h.crashes],
                ["Ad rules", h.filter_counts?.ads], ["Tracking rules", h.filter_counts?.tracking]].map(([k, v]) => (
                <div key={k} className="bg-zinc-900 rounded-lg p-2"><div className="text-[11px] text-zinc-500">{k}</div><div className="font-medium">{v}</div></div>))}
            </div>
          )}
          <button data-testid="cloud-filters-update" disabled={!!busy} onClick={() => run("filters", async () => { const { data } = await api.post("/admin/cloud/filters/update"); toast.success(`Filters: ${Object.entries(data.result).map(([k, v]) => `${k} ${v}`).join(", ")}`); })}
            className="text-sm border border-zinc-700 rounded-lg px-3 py-2 hover:bg-zinc-800 flex items-center gap-2 disabled:opacity-50">{busy === "filters" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />} Update EasyList / EasyPrivacy</button>
        </div>
      </div>
      <NightlyCard />
      <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-5">
        <h3 className="font-head font-semibold flex items-center gap-2 mb-1"><Activity className="w-4 h-4" /> Load test</h3>
        <p className="text-xs text-zinc-500 mb-3">Starts headless sessions on the Simpsons test site, samples resources for 20 s, then ends them. Run 1, 2, then 3 before trying more.</p>
        <div className="flex items-center gap-2 mb-4">
          <input data-testid="cloud-loadtest-count" type="number" min={1} max={25} value={n} onChange={(e) => setN(Number(e.target.value))} className="w-20 bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1.5" />
          <button data-testid="cloud-loadtest-run" disabled={!!busy} onClick={() => run("load", async () => { const { data } = await api.post("/admin/cloud/load-test", { sessions: n }); toast.success(`${data.started_ok}/${data.sessions} sessions ran`); })}
            className="text-sm bg-volt text-volt-ink font-semibold rounded-lg px-4 py-1.5 disabled:opacity-50 flex items-center gap-2">{busy === "load" && <Loader2 className="w-4 h-4 animate-spin" />} Run load test</button>
        </div>
        {!!lt.length && (
          <div className="overflow-x-auto"><table className="w-full text-xs" data-testid="cloud-loadtests">
            <thead className="text-zinc-500 text-left"><tr><th className="py-1 pr-3">When</th><th className="pr-3">Sessions</th><th className="pr-3">Started</th><th className="pr-3">Avg fps</th><th className="pr-3">Peak Chromium CPU</th><th className="pr-3">Peak Chromium RAM</th><th className="pr-3">Peak server CPU</th><th>Min free RAM</th></tr></thead>
            <tbody>{lt.map((r, i) => <tr key={i} className="border-t border-zinc-800"><td className="py-1 pr-3">{new Date(r.ran_at).toLocaleString()}</td><td>{r.sessions}</td><td>{r.started_ok}</td><td>{r.avg_fps}</td><td>{r.peak_chromium_cpu_percent}%</td><td>{r.peak_chromium_rss_mb} MB</td><td>{r.peak_system_cpu_percent}%</td><td>{r.min_mem_available_mb} MB</td></tr>)}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

export function AdminCloud() {
  const [sub, setSub] = useState("overview");
  const tabs = [["overview", "Overview"], ["games", "Games"], ["sessions", "Sessions"], ["events", "Security log"]];
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 sm:p-6" data-testid="admin-cloud">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div><h2 className="font-head text-xl font-bold">Protected Cloud Browser</h2>
          <p className="text-sm text-zinc-500">Games run in isolated Chromium sessions on your server and stream to players. Picture and game sound are streamed to players; ads, trackers, pop-ups and redirects are filtered.</p></div>
        <div className="flex gap-1 bg-zinc-950 rounded-lg p-1">{tabs.map(([k, l]) => <button key={k} data-testid={`cloud-tab-${k}`} onClick={() => setSub(k)} className={`text-sm px-3 py-1.5 rounded-md ${sub === k ? "bg-zinc-800 text-white" : "text-zinc-400"}`}>{l}</button>)}</div>
      </div>
      {sub === "overview" && <CloudOverview />}
      {sub === "games" && <CloudGames />}
      {sub === "sessions" && <CloudSessions />}
      {sub === "events" && <CloudEvents />}
    </div>
  );
}

